import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, FloppyDisk, MagnifyingGlass, Leaf, UploadSimple, FileText, Image, X, CaretDown, CaretUp, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Md } from '@/components/Md';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const MEAL_TYPE_OPTIONS = [
  { key: 'pequeno_almoco', label: 'P.almoço' },
  { key: 'lanche_manha', label: 'L.manhã' },
  { key: 'almoco', label: 'Almoço' },
  { key: 'lanche_tarde', label: 'L.tarde' },
  { key: 'jantar', label: 'Jantar' },
  { key: 'ceia', label: 'Ceia' },
];



const foodCats = (food) => (food.allergen_categories?.length ? food.allergen_categories : (food.allergen_category ? [food.allergen_category] : []));

function AllergenBadge({ food }) {
  if (!food.is_allergen) return null;
  return (
    <span className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0" data-testid={`allergen-badge-${food.id}`}>
      <Warning size={8} weight="fill" /> {foodCats(food).join(' + ')}
    </span>
  );
}

function ExcludedBadge({ food }) {
  if (!food.exclude_from_generation) return null;
  return (
    <span className="text-[8px] bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid={`excluded-badge-${food.id}`}>
      Excluído da geração
    </span>
  );
}

function VitaminCBadge({ food }) {
  if (!food.rich_in_vitamin_c) return null;
  return (
    <span className="text-[8px] bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid={`vitc-badge-${food.id}`}>
      Vitamina C
    </span>
  );
}

function AllergenIntroSection({ food, protocols }) {
  if (!food.is_allergen) return null;
  const cats = foodCats(food);
  return (
    <div className="mt-2 pt-2 border-t border-amber-200/50 bg-amber-50/30 rounded-lg p-2" data-testid={`allergen-intro-${food.id}`}>
      <p className="text-[9px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Como introduzir ({cats.join(' + ')})</p>
      {cats.length > 1 ? (
        <p className="text-[9px] text-amber-800 leading-relaxed" data-testid={`allergen-intro-multi-${food.id}`}>Alimento com vários alergénios: só entra nas ementas depois de todos terem tido as 3 exposições iniciais (cada um introduzido com um alimento simples). Nunca é usado como refeição de introdução.</p>
      ) : (
        <p className="text-[9px] text-amber-800 leading-relaxed">{protocols.by_category?.[cats[0]] || protocols.default_text}</p>
      )}
    </div>
  );
}

function ArticlesSection({ food, foodsRef, setFoods }) {
  const confirmDelete = useConfirmDelete();
  const [showAdd, setShowAdd] = useState(false);
  const [artType, setArtType] = useState('text');
  const [artText, setArtText] = useState('');
  const [uploading, setUploading] = useState(false);
  const getFresh = () => foodsRef.current.find(x => x.id === food.id) || food;
  const articles = getFresh().articles || [];

  const applyUpdate = async (updated) => {
    const fresh = getFresh();
    const newFood = { ...fresh, articles: updated };
    foodsRef.current = foodsRef.current.map(x => x.id === food.id ? newFood : x);
    setFoods(foodsRef.current);
    try { await axios.put(`${API}/api/baby-foods/${food.id}`, { articles: updated }); } catch { toast.error('Erro ao guardar'); }
  };

  const addTextArticle = async () => {
    if (!artText.trim()) return;
    const current = getFresh().articles || [];
    const updated = [...current, { id: Date.now().toString(), type: 'text', content: artText, created_at: new Date().toISOString() }];
    await applyUpdate(updated);
    setArtText(''); setShowAdd(false);
  };

  const addFileArticle = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: up } = await axios.post(`${API}/api/upload`, fd);
      const isImage = file.type.startsWith('image/');
      const current = getFresh().articles || [];
      const updated = [...current, { id: Date.now().toString(), type: isImage ? 'image' : 'file', path: up.path, name: file.name, created_at: new Date().toISOString() }];
      await applyUpdate(updated);
      setShowAdd(false);
    } catch { toast.error('Erro ao carregar ficheiro'); }
    finally { setUploading(false); }
  };

  const removeArticle = async (artId) => {
    const current = getFresh().articles || [];
    const art = current.find(a => a.id === artId);
    const ok = await confirmDelete({ description: `Vai apagar o artigo "${art?.name || art?.content?.slice(0, 40) || ''}" de ${food.name}.` });
    if (!ok) return;
    const updated = current.filter(a => a.id !== artId);
    await applyUpdate(updated);
  };

  return (
    <div className="mt-2 pt-2 border-t border-brand-border/50">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] text-brand-muted uppercase tracking-wider">Artigos</span>
        <button onClick={() => setShowAdd(!showAdd)} className="text-[9px] text-brand-green hover:underline flex items-center gap-0.5" data-testid={`add-article-${food.id}`}>
          <Plus size={8} /> Adicionar
        </button>
      </div>
      {articles.length === 0 && !showAdd && <p className="text-[9px] text-brand-placeholder italic">Sem artigos</p>}
      {articles.map(a => (
        <div key={a.id} className="flex items-start gap-2 py-1 group">
          {a.type === 'text' && <FileText size={10} className="text-brand-muted mt-0.5 flex-shrink-0" />}
          {a.type === 'image' && <Image size={10} className="text-brand-muted mt-0.5 flex-shrink-0" />}
          {a.type === 'file' && <UploadSimple size={10} className="text-brand-muted mt-0.5 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            {a.type === 'text' && <Md as="p" className="text-[10px] text-brand-text">{a.content}</Md>}
            {a.type === 'image' && <img src={`${API}/api/files/${a.path}`} alt={a.name} className="w-16 h-16 rounded-lg object-cover" onError={e => { e.target.style.display='none'; }} />}
            {a.type === 'file' && <a href={`${API}/api/files/${a.path}`} target="_blank" rel="noreferrer" className="text-[10px] text-brand-green hover:underline">{a.name}</a>}
          </div>
          <button onClick={() => removeArticle(a.id)} className="hidden group-hover:block text-brand-placeholder hover:text-red-500"><X size={10} /></button>
        </div>
      ))}
      {showAdd && (
        <div className="bg-brand-bg/50 rounded-lg p-2 mt-1 space-y-2">
          <div className="flex gap-1">
            {['text', 'image', 'file'].map(t => (
              <button key={t} onClick={() => setArtType(t)} className={`px-2 py-1 rounded text-[9px] font-medium ${artType === t ? 'bg-brand-green text-white' : 'bg-white text-brand-muted border border-brand-border'}`}>
                {t === 'text' ? 'Texto' : t === 'image' ? 'Imagem' : 'Ficheiro'}
              </button>
            ))}
          </div>
          {artType === 'text' ? (
            <div className="flex gap-1">
              <Input value={artText} onChange={e => setArtText(e.target.value)} placeholder="Texto do artigo..." className="text-[10px] rounded-lg border-brand-border h-7 flex-1" />
              <button onClick={addTextArticle} className="bg-brand-green text-white rounded-lg px-2 h-7 text-[9px]">OK</button>
            </div>
          ) : (
            <label className="block">
              <div className="border border-dashed border-brand-border rounded-lg p-2 text-center text-[9px] text-brand-muted cursor-pointer hover:bg-brand-bg">
                {uploading ? 'A carregar...' : `Escolher ${artType === 'image' ? 'imagem' : 'ficheiro'}...`}
              </div>
              <input type="file" accept={artType === 'image' ? 'image/*' : '*'} onChange={e => e.target.files?.[0] && addFileArticle(e.target.files[0])} className="hidden" />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export default function BabyFoodsPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [foods, setFoods] = useState([]);
  const [groups, setGroups] = useState([]);
  const foodsRef = useRef([]);
  useEffect(() => { foodsRef.current = foods; }, [foods]);
  const groupsRef = useRef([]);
  useEffect(() => { groupsRef.current = groups; }, [groups]);
  const [filter, setFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editFood, setEditFood] = useState(null);
  const [form, setForm] = useState({ name: '', group: '', months: [], is_allergen: false, allergen_category: '', daily_use: false });
  const [allergenCategories, setAllergenCategories] = useState([]);
  const [showGroupMgmt, setShowGroupMgmt] = useState(false);
  const [newGroupKey, setNewGroupKey] = useState('');
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupLabel, setEditGroupLabel] = useState('');
  const [expandedFood, setExpandedFood] = useState(null);
  const [protocols, setProtocols] = useState({ default_text: '', by_category: {} });
  const [showProtocolMgmt, setShowProtocolMgmt] = useState(false);
  const [protocolDrafts, setProtocolDrafts] = useState({});
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [clinicalTags, setClinicalTags] = useState([]);
  const [showClinicalTags, setShowClinicalTags] = useState(false);
  const [untagged, setUntagged] = useState({ count: 0, foods: [] });
  const [newTag, setNewTag] = useState({ label: '', description: '' });
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [dupKeepChoice, setDupKeepChoice] = useState({});

  const fetchDuplicates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/baby-foods/duplicates`);
      setDuplicates(data.clusters || []);
    } catch { }
  }, []);
  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates]);

  const fetch = useCallback(async () => {
    try {
      const [fRes, gRes, aRes, pRes] = await Promise.all([
        axios.get(`${API}/api/baby-foods`),
        axios.get(`${API}/api/baby-food-groups`),
        axios.get(`${API}/api/baby-allergen-categories`),
        axios.get(`${API}/api/baby-allergen-intro-protocols`),
      ]);
      setFoods(fRes.data);
      setGroups(gRes.data);
      setAllergenCategories(aRes.data);
      setProtocols(pRes.data);
    } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const fetchClinical = useCallback(async () => {
    try {
      const [tRes, uRes] = await Promise.all([axios.get(`${API}/api/baby-clinical-tags`), axios.get(`${API}/api/admin/baby-foods/clinical-untagged`)]);
      setClinicalTags(tRes.data);
      setUntagged(uRes.data);
    } catch {}
  }, []);
  useEffect(() => { fetchClinical(); }, [fetchClinical]);

  const saveClinicalTags = async (tags) => {
    try {
      const { data } = await axios.put(`${API}/api/admin/baby-clinical-tags`, { tags });
      setClinicalTags(data);
      toast.success('Etiquetas guardadas');
    } catch { toast.error('Erro ao guardar etiquetas'); }
  };

  const saveQuantities = async (category) => {
    const current = qtyDrafts[category] ?? (protocols.quantities_by_category?.[category] || protocols.default_quantities || ['', '', '']);
    try {
      await axios.put(`${API}/api/admin/baby-allergen-intro-quantities/${encodeURIComponent(category)}`, { quantities: current });
      toast.success('Quantidades guardadas');
      setQtyDrafts(prev => { const n = { ...prev }; delete n[category]; return n; });
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar quantidades'); }
  };

  const saveProtocol = async (category) => {
    const text = protocolDrafts[category] ?? (protocols.by_category?.[category] || protocols.default_text);
    try {
      await axios.put(`${API}/api/admin/baby-allergen-intro-protocols/${encodeURIComponent(category)}`, { text });
      toast.success('Protocolo guardado');
      fetch();
    } catch { toast.error('Erro ao guardar'); }
  };

  const groupLabels = {};
  groups.forEach(g => { groupLabels[g.key] = g.label; });

  const toggleMonth = (m) => {
    setForm(prev => {
      const arr = Array.isArray(prev.months) ? prev.months : [];
      const next = arr.includes(m) ? arr.filter(x => x !== m) : [...arr, m].sort((a, b) => a - b);
      return { ...prev, months: next, all_year: next.length === 12 };
    });
  };
  const selectAllMonths = () => setForm(prev => ({ ...prev, months: [1,2,3,4,5,6,7,8,9,10,11,12], all_year: true }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      let res;
      if (editFood) {
        res = await axios.put(`${API}/api/baby-foods/${editFood.id}`, form);
        toast.success('Alimento atualizado');
      } else {
        res = await axios.post(`${API}/api/baby-foods`, form);
        toast.success('Alimento adicionado');
      }
      if (res?.data?.duplicate_warning) toast.warning(res.data.duplicate_warning);
      setShowAdd(false); setEditFood(null);
      setForm({ name: '', group: groups[0]?.key || '', months: [] });
      fetch(); fetchDuplicates();
    } catch { toast.error('Erro ao guardar'); }
  };

  const markNotDuplicate = async (foodIds) => {
    try {
      await axios.post(`${API}/api/admin/baby-foods/duplicates/not-duplicate`, { food_ids: foodIds });
      toast.success('Marcado como não duplicado');
      fetchDuplicates();
    } catch { toast.error('Erro'); }
  };

  const mergeDuplicates = async (cluster) => {
    const keepId = dupKeepChoice[cluster.normalized_name] || cluster.foods[0].id;
    const removeIds = cluster.foods.map(f => f.id).filter(id => id !== keepId);
    const ok = await confirmDelete({ description: `Vai juntar ${cluster.foods.length} alimentos num só, migrando ementas e introduções. Pode desfazer em "Apagados recentemente".` });
    if (!ok) return;
    try {
      for (const removeId of removeIds) {
        await axios.post(`${API}/api/admin/baby-foods/duplicates/merge`, { keep_id: keepId, remove_id: removeId });
      }
      toast.success('Alimentos juntados');
      fetch(); fetchDuplicates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao juntar'); }
  };

  const handleDelete = async (id) => {
    const food = foods.find(f => f.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o alimento "${food?.name || ''}" da biblioteca de bebés.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/baby-foods/${id}`); toast.success('Removido'); fetch(); fetchDuplicates(); } catch { toast.error('Erro'); }
  };

  const openEdit = (f) => {
    const seasonalMonths = Array.isArray(f.months) ? f.months : [];
    const minAge = typeof f.min_age === 'number' ? f.min_age : (typeof f.months === 'number' ? f.months : '');
    const tex = (f.texture && typeof f.texture === 'object' && !Array.isArray(f.texture)) ? f.texture : { fase1: [], fase2: [], fase3: [] };
    setEditFood(f); setForm({ name: f.name || '', group: f.group || '', months: seasonalMonths, all_year: f.all_year || false, min_age: minAge, is_allergen: f.is_allergen || false, allergen_category: f.allergen_category || '', allergen_categories: foodCats(f), fish_type: f.fish_type || '', daily_use: f.daily_use || false, exclude_from_generation: f.exclude_from_generation || false, rich_in_vitamin_c: f.rich_in_vitamin_c || false, clinical_tags: f.clinical_tags || [], description: f.description || '', texture: tex, preparation_tips: f.preparation_tips || '', portions: f.portions || {} }); setShowAdd(true);
  };
  const openAdd = () => {
    setEditFood(null); setForm({ name: '', group: groupFilter || groups[0]?.key || '', months: [], all_year: false, min_age: '', is_allergen: false, allergen_category: '', allergen_categories: [], fish_type: '', daily_use: false, exclude_from_generation: false, rich_in_vitamin_c: false, description: '', texture: { fase1: [], fase2: [], fase3: [] }, preparation_tips: '', portions: {} }); setShowAdd(true);
  };

  // Group management
  const addGroup = async () => {
    if (!newGroupKey.trim() || !newGroupLabel.trim()) { toast.error('Key e label obrigatórios'); return; }
    try {
      await axios.post(`${API}/api/baby-food-groups`, { key: newGroupKey.toLowerCase().replace(/ /g, '_'), label: newGroupLabel });
      toast.success('Grupo criado');
      setNewGroupKey(''); setNewGroupLabel(''); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const saveGroupLabel = async (groupId) => {
    try { await axios.put(`${API}/api/baby-food-groups/${groupId}`, { label: editGroupLabel }); toast.success('Grupo atualizado'); setEditGroupId(null); fetch(); } catch { toast.error('Erro'); }
  };
  const deleteGroup = async (groupId) => {
    const g = groups.find(x => x.id === groupId);
    const ok = await confirmDelete({ description: `Vai apagar o grupo "${g?.label || ''}". Os alimentos deste grupo podem ficar sem grupo.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/baby-food-groups/${groupId}`); toast.success('Grupo eliminado'); fetch(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const filtered = foods.filter(f => {
    if (groupFilter && f.group !== groupFilter) return false;
    if (filter && !f.name.toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });
  const grouped = {};
  filtered.forEach(f => { if (!grouped[f.group]) grouped[f.group] = []; grouped[f.group].push(f); });

  return (
    <div className="fade-in" data-testid="baby-foods-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Biblioteca de Alimentos: Bebés</h2>
          <p className="text-xs text-brand-muted mt-1">Alimentos para introdução alimentar com sazonalidade</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowProtocolMgmt(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="manage-protocols-btn">
            <Warning size={14} /> Protocolos de introdução
          </button>
          <button onClick={() => { fetchClinical(); setShowClinicalTags(true); }} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="manage-clinical-tags-btn">
            <Leaf size={14} /> Etiquetas clínicas{untagged.count > 0 ? ` (${untagged.count} por etiquetar)` : ''}
          </button>
          <button onClick={() => setShowGroupMgmt(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2.5 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="manage-groups-btn">
            <PencilSimple size={14} /> Gerir grupos
          </button>
          <button onClick={openAdd} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-baby-food-btn">
            <Plus size={16} /> Adicionar
          </button>
        </div>
      </div>

      {duplicates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="duplicates-banner">
          <p className="text-xs text-amber-800 flex items-center gap-1.5"><Warning size={14} weight="fill" /> {duplicates.length} {duplicates.length > 1 ? 'possíveis' : 'possível'} duplicado{duplicates.length > 1 ? 's' : ''} na biblioteca</p>
          <button onClick={() => setShowDuplicates(true)} className="bg-amber-600 text-white rounded-full px-3 py-1.5 text-xs font-medium" data-testid="review-duplicates-btn">Rever duplicados</button>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Pesquisar alimento..." className="pl-8 rounded-xl border-brand-border text-sm" data-testid="baby-food-search" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          <button onClick={() => setGroupFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${!groupFilter ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Todos</button>
          {groups.map(g => (
            <button key={g.key} onClick={() => setGroupFilter(groupFilter === g.key ? '' : g.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${groupFilter === g.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {groups.map(g => {
          const items = grouped[g.key];
          if (!items || items.length === 0) return null;
          return (
            <div key={g.key} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`baby-group-${g.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center border bg-brand-bg text-brand-green"><Leaf size={14} weight="duotone" /></div>
                <h3 className="font-heading font-medium text-sm text-brand-text">{g.label}</h3>
                <span className="text-[10px] text-brand-muted bg-brand-bg px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {MEAL_TYPE_OPTIONS.map(mt => {
                  const active = (g.meal_types || []).includes(mt.key);
                  const required = (g.required_meal_types || []).includes(mt.key);
                  return (
                    <div key={mt.key} className="flex items-center gap-0.5">
                      <button onClick={async () => {
                        const fresh = groupsRef.current.find(x => x.id === g.id) || g;
                        const isActive = (fresh.meal_types || []).includes(mt.key);
                        const current = fresh.meal_types || [];
                        const updated = isActive ? current.filter(k => k !== mt.key) : [...current, mt.key];
                        const reqUpdated = isActive ? (fresh.required_meal_types || []).filter(k => k !== mt.key) : (fresh.required_meal_types || []);
                        const newGroup = { ...fresh, meal_types: updated, required_meal_types: reqUpdated };
                        groupsRef.current = groupsRef.current.map(x => x.id === g.id ? newGroup : x);
                        setGroups(groupsRef.current);
                        try { await axios.put(`${API}/api/baby-food-groups/${g.id}`, { meal_types: updated, required_meal_types: reqUpdated }); } catch { fetch(); }
                      }} className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${active ? 'bg-brand-green/15 text-brand-green border border-brand-green/30' : 'bg-brand-bg/50 text-brand-placeholder border border-transparent'}`} data-testid={`group-meal-${g.key}-${mt.key}`}>
                        {mt.label}
                      </button>
                      {active && (
                        <button title="Obrigatório para o motor de IA" onClick={async () => {
                          const fresh = groupsRef.current.find(x => x.id === g.id) || g;
                          const isRequired = (fresh.required_meal_types || []).includes(mt.key);
                          const current = fresh.required_meal_types || [];
                          const updated = isRequired ? current.filter(k => k !== mt.key) : [...current, mt.key];
                          const newGroup = { ...fresh, required_meal_types: updated };
                          groupsRef.current = groupsRef.current.map(x => x.id === g.id ? newGroup : x);
                          setGroups(groupsRef.current);
                          try { await axios.put(`${API}/api/baby-food-groups/${g.id}`, { required_meal_types: updated }); } catch { fetch(); }
                        }} className={`px-1 py-0.5 rounded-full text-[8px] font-bold ${required ? 'bg-amber-500 text-white' : 'bg-brand-bg/50 text-brand-placeholder border border-transparent'}`} data-testid={`group-required-${g.key}-${mt.key}`}>
                          Obg
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1">
                {items.map(f => {
                  const seasonalM = Array.isArray(f.months) ? f.months : [];
                  const monthStr = seasonalM.length === 0 || seasonalM.length === 12 ? '' : seasonalM.map(m => MONTHS_PT[m-1]).join(', ');
                  const allYear = f.all_year || seasonalM.length === 12;
                  const seasonalGroup = /fruta|hortic/.test(`${g.key} ${g.label}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase());
                  const noSeason = seasonalGroup && !allYear && seasonalM.length === 0;
                  const ageStr = f.min_age ? `A partir dos ${f.min_age}m` : (typeof f.months === 'number' ? `A partir dos ${f.months}m` : '');
                  const isExpanded = expandedFood === f.id;
                  const hasArticles = (f.articles || []).length > 0;
                  return (
                    <div key={f.id} data-testid={`baby-food-${f.id}`}>
                      <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-brand-bg/50 group">
                        <button onClick={() => setExpandedFood(isExpanded ? null : f.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                          <span className="text-sm text-brand-text">{f.name}</span>
                          <AllergenBadge food={f} />
                          <VitaminCBadge food={f} />
                          <ExcludedBadge food={f} />
                          {ageStr && <span className="text-[9px] text-brand-placeholder">{ageStr}</span>}
                          {monthStr && <span className="text-[9px] text-brand-placeholder">{monthStr}</span>}
                          {allYear && <span className="text-[10px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full" data-testid={`baby-food-all-year-badge-${f.id}`}>Todo o ano</span>}
                          {noSeason && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" title="Sem meses marcados: não entra nas ementas geradas" data-testid={`baby-food-no-season-${f.id}`}>Sem época: fora das ementas</span>}
                          {hasArticles && <span className="text-[10px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full">{f.articles.length}</span>}
                        </button>
                        <div className="hidden group-hover:flex items-center gap-1">
                          <button onClick={() => openEdit(f)} className="text-brand-muted hover:text-brand-green p-0.5"><PencilSimple size={12} /></button>
                          <button onClick={() => handleDelete(f.id)} className="text-brand-muted hover:text-red-500 p-0.5"><Trash size={12} /></button>
                        </div>
                        {isExpanded ? <CaretUp size={12} className="text-brand-muted" /> : <CaretDown size={12} className="text-brand-muted" />}
                      </div>
                      {isExpanded && (
                        <div className="ml-4 mr-2">
                          <AllergenIntroSection food={f} protocols={protocols} />
                          <ArticlesSection food={f} foodsRef={foodsRef} setFoods={setFoods} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/edit food dialog */}
      <Dialog open={showAdd} onOpenChange={(v) => { setShowAdd(v); if (!v) setEditFood(null); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">{editFood ? 'Editar alimento' : 'Novo alimento'}</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Dados nutricionais, sazonalidade e alergénios do alimento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Nome</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Brócolos" className="rounded-xl border-brand-border" data-testid="baby-food-name-input" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Grupo</Label>
              <select value={form.group} onChange={e => setForm({ ...form, group: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="baby-food-group-select">
                {groups.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            {/* Allergen */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="allergen-toggle">
                <input type="checkbox" checked={form.is_allergen} onChange={e => setForm({ ...form, is_allergen: e.target.checked, allergen_category: e.target.checked ? form.allergen_category : '', allergen_categories: e.target.checked ? (form.allergen_categories || []) : [] })} className="w-4 h-4 rounded border-brand-border text-amber-600 focus:ring-amber-500" />
                <span className="text-xs text-brand-muted">Alergéneo</span>
              </label>
              {form.is_allergen && (
                <div className="mt-2" data-testid="allergen-categories-picker">
                  <p className="text-[10px] text-brand-muted mb-1">Categorias de alergénio (podes escolher várias, ex.: panquecas = Ovo + Leite de vaca + Cereais com glúten)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {allergenCategories.map(c => {
                      const on = (form.allergen_categories || []).includes(c);
                      return (
                        <button key={c} type="button" onClick={() => { const next = on ? form.allergen_categories.filter(x => x !== c) : [...(form.allergen_categories || []), c]; setForm({ ...form, allergen_categories: next, allergen_category: next[0] || '' }); }}
                          className={`text-[10px] px-2.5 py-1 rounded-full border ${on ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-200 text-amber-700 bg-amber-50'}`} data-testid={`allergen-category-toggle-${c}`}>{c}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              {form.is_allergen && (form.allergen_categories || []).includes('Peixe') && (
                <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="fish-type-toggle">
                  <input type="checkbox" checked={form.fish_type === 'gordo'} onChange={e => setForm({ ...form, fish_type: e.target.checked ? 'gordo' : 'branco' })} className="w-4 h-4 rounded border-brand-border text-amber-600 focus:ring-amber-500" />
                  <span className="text-xs text-brand-muted">Peixe gordo?</span>
                </label>
              )}
              <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="daily-use-toggle">
                <input type="checkbox" checked={form.daily_use} onChange={e => setForm({ ...form, daily_use: e.target.checked })} className="w-4 h-4 rounded border-brand-border text-brand-green focus:ring-brand-green" />
                <span className="text-xs text-brand-muted">Uso diário (isento do limite de 3x/semana e pode repetir-se no mesmo dia)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="vitamin-c-toggle">
                <input type="checkbox" checked={!!form.rich_in_vitamin_c} onChange={e => setForm({ ...form, rich_in_vitamin_c: e.target.checked })} className="w-4 h-4 rounded border-brand-border text-orange-600 focus:ring-orange-500" />
                <span className="text-xs text-brand-muted">Rico em vitamina C (ajuda a garantir 1 alimento rico em vit. C ao almoço e jantar em casa)</span>
              </label>
              {clinicalTags.length > 0 && (
                <div className="mt-3" data-testid="clinical-tags-picker">
                  <Label className="text-xs text-brand-muted mb-1 block">Etiquetas clínicas (usadas pelas regras do motor e pelo Semáforo)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {clinicalTags.map(t => {
                      const on = (form.clinical_tags || []).includes(t.key);
                      return (
                        <button key={t.key} type="button" title={t.description}
                          onClick={() => setForm({ ...form, clinical_tags: on ? form.clinical_tags.filter(k => k !== t.key) : [...(form.clinical_tags || []), t.key] })}
                          className={`text-[10px] px-2.5 py-1 rounded-full border ${on ? 'bg-brand-green text-white border-brand-green' : 'border-brand-border text-brand-muted'}`}
                          data-testid={`clinical-tag-toggle-${t.key}`}>{t.label}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="exclude-generation-toggle">
                <input type="checkbox" checked={!!form.exclude_from_generation} onChange={e => setForm({ ...form, exclude_from_generation: e.target.checked })} className="w-4 h-4 rounded border-brand-border text-red-600 focus:ring-red-500" />
                <span className="text-xs text-brand-muted">Excluir da geração automática (nunca escolhido pelo motor em novas ementas)</span>
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-brand-muted">Sazonalidade</Label>
                <button type="button" onClick={selectAllMonths} className={`text-[10px] hover:underline ${form.all_year ? 'text-brand-green font-semibold' : 'text-brand-green'}`} data-testid="baby-food-all-year-btn">{form.all_year ? '✓ Todo o ano' : 'Todo o ano'}</button>
              </div>
              <div className="grid grid-cols-6 gap-1.5">
                {MONTHS_PT.map((m, i) => {
                  const monthsArr = Array.isArray(form.months) ? form.months : [];
                  return (
                    <button key={i} type="button" onClick={() => toggleMonth(i + 1)}
                      className={`py-1.5 rounded-lg text-xs font-medium ${monthsArr.includes(i + 1) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Enriched fields */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Descrição / Notas clínicas</Label>
              <Textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Informação nutricional, benefícios, quando evitar..." className="rounded-xl border-brand-border" rows={2} />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Idade mínima (meses)</Label>
              <Input type="number" min="1" max="24" value={form.min_age ?? ''} onChange={e => { const v = e.target.value; setForm({ ...form, min_age: v === '' ? '' : (Number.isFinite(parseInt(v)) ? parseInt(v) : '') }); }} placeholder="Ex: 6" className="w-20 rounded-xl border-brand-border" data-testid="baby-food-min-age-input" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Textura recomendada por fase</Label>
              {[{key: 'fase1', label: 'Fase 1 (6-9m)', opts: ['Puré', 'Esmagado', 'BLW']}, {key: 'fase2', label: 'Fase 2 (9-12m)', opts: ['Esmagado', 'Pedaços', 'BLW']}, {key: 'fase3', label: 'Fase 3 (12-24m)', opts: ['Pedaços', 'Inteiro', 'BLW']}].map(fase => {
                const tex = typeof form.texture === 'object' && form.texture !== null ? form.texture : { fase1: [], fase2: [], fase3: [] };
                const selected = tex[fase.key] || [];
                return (
                  <div key={fase.key} className="mb-2">
                    <p className="text-[9px] text-brand-muted mb-0.5">{fase.label}</p>
                    <div className="flex gap-1">
                      {fase.opts.map(o => {
                        const active = selected.includes(o);
                        return <button key={o} type="button" onClick={() => {
                          const updated = active ? selected.filter(x => x !== o) : [...selected, o];
                          setForm({ ...form, texture: { ...tex, [fase.key]: updated } });
                        }} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${active ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o}</button>;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Sugestões de corte e preparação</Label>
              <Textarea value={form.preparation_tips || ''} onChange={e => setForm({ ...form, preparation_tips: e.target.value })} placeholder="Ex: Cortar em palitos para BLW, cozer 8 min..." className="rounded-xl border-brand-border" rows={2} />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Porção adequada por fase</Label>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">Fase 1 (6-9m)</Label><Input value={form.portions?.['fase1'] || ''} onChange={e => setForm({ ...form, portions: { ...form.portions, 'fase1': e.target.value } })} placeholder="Ex: 2-3 colheres" className="rounded-lg border-brand-border text-xs h-7" /></div>
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">Fase 2 (9-12m)</Label><Input value={form.portions?.['fase2'] || ''} onChange={e => setForm({ ...form, portions: { ...form.portions, 'fase2': e.target.value } })} placeholder="Ex: 4-5 colheres" className="rounded-lg border-brand-border text-xs h-7" /></div>
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">Fase 3 (12-24m)</Label><Input value={form.portions?.['fase3'] || ''} onChange={e => setForm({ ...form, portions: { ...form.portions, 'fase3': e.target.value } })} placeholder="Ex: 1/2 porção adulto" className="rounded-lg border-brand-border text-xs h-7" /></div>
              </div>
            </div>
            <button onClick={handleSave} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-baby-food-btn">
              {editFood ? 'Guardar alterações' : 'Adicionar alimento'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group management dialog */}
      <Dialog open={showGroupMgmt} onOpenChange={setShowGroupMgmt}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerir Grupos Alimentares</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {groups.map(g => (
              <div key={g.id} className="flex items-center gap-2 p-2 bg-brand-bg/50 rounded-xl" data-testid={`group-mgmt-${g.key}`}>
                {editGroupId === g.id ? (
                  <>
                    <Input value={editGroupLabel} onChange={e => setEditGroupLabel(e.target.value)} className="text-sm rounded-lg border-brand-border h-8 flex-1" autoFocus />
                    <button onClick={() => saveGroupLabel(g.id)} className="text-brand-green p-1"><FloppyDisk size={14} /></button>
                    <button onClick={() => setEditGroupId(null)} className="text-brand-muted p-1"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-brand-text flex-1">{g.label}</span>
                    <span className="text-[9px] text-brand-placeholder">{g.key}</span>
                    <button onClick={() => { setEditGroupId(g.id); setEditGroupLabel(g.label); }} className="text-brand-muted hover:text-brand-green p-1"><PencilSimple size={13} /></button>
                    <button onClick={() => deleteGroup(g.id)} className="text-brand-muted hover:text-red-500 p-1"><Trash size={13} /></button>
                  </>
                )}
              </div>
            ))}
            <div className="border-t border-brand-border pt-3">
              <p className="text-xs text-brand-muted mb-2">Novo grupo</p>
              <div className="flex gap-2">
                <Input value={newGroupKey} onChange={e => setNewGroupKey(e.target.value)} placeholder="Key (ex: fruta)" className="rounded-lg border-brand-border text-sm h-9 flex-1" data-testid="new-group-key" />
                <Input value={newGroupLabel} onChange={e => setNewGroupLabel(e.target.value)} placeholder="Nome (ex: Frutas)" className="rounded-lg border-brand-border text-sm h-9 flex-1" data-testid="new-group-label" />
                <button onClick={addGroup} className="bg-brand-green text-white rounded-lg px-3 h-9 text-xs" data-testid="add-group-btn"><Plus size={14} /></button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Allergen intro protocol management dialog */}
      <Dialog open={showProtocolMgmt} onOpenChange={setShowProtocolMgmt}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Protocolos de introdução de alergénios</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">1 texto por categoria de alergénio, usado na ficha do alimento e na app dos pais.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-brand-muted mb-3">1 texto por categoria de alergénio. Fica visível na ficha do alimento, na app dos pais e (se relevante) na ementa. Deixa em branco para usar o texto padrão.</p>
          <div className="space-y-4">
            {allergenCategories.map(cat => (
              <div key={cat} data-testid={`protocol-editor-${cat}`}>
                <Label className="text-xs text-brand-text font-medium mb-1 block">{cat}</Label>
                <Textarea
                  value={protocolDrafts[cat] ?? (protocols.by_category?.[cat] || protocols.default_text)}
                  onChange={e => setProtocolDrafts({ ...protocolDrafts, [cat]: e.target.value })}
                  rows={3} className="rounded-xl border-brand-border text-xs" />
                <button onClick={() => saveProtocol(cat)} className="mt-1 text-[10px] text-brand-green font-medium hover:underline" data-testid={`save-protocol-${cat}`}>Guardar</button>
                <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`quantities-editor-${cat}`}>
                  {[0, 1, 2].map(n => {
                    const cur = qtyDrafts[cat] ?? (protocols.quantities_by_category?.[cat] || protocols.default_quantities || ['', '', '']);
                    return (
                      <div key={n}>
                        <Label className="text-[9px] text-brand-placeholder block mb-0.5">{n + 1}.ª exposição</Label>
                        <Input value={cur[n] || ''} onChange={e => { const next = [...cur]; next[n] = e.target.value; setQtyDrafts({ ...qtyDrafts, [cat]: next }); }} className="rounded-lg border-brand-border text-xs h-7" data-testid={`quantity-input-${cat}-${n + 1}`} />
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => saveQuantities(cat)} className="mt-1 text-[10px] text-brand-green font-medium hover:underline" data-testid={`save-quantities-${cat}`}>Guardar quantidades</button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showClinicalTags} onOpenChange={setShowClinicalTags}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-2xl" data-testid="clinical-tags-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Etiquetas clínicas</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Complementam os grupos da Biblioteca (Energia, Hortícolas, Frutas...), que já definem hidratos, hortícolas e frutas. Usadas pelas regras do motor e pelo Semáforo. As 4 etiquetas do sistema não podem ser apagadas, mas podes editar o nome e a descrição.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {clinicalTags.map((t, idx) => (
              <div key={t.key} className="grid grid-cols-[1fr_2fr] gap-2" data-testid={`clinical-tag-row-${t.key}`}>
                <Input value={t.label} onChange={e => setClinicalTags(clinicalTags.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} className="rounded-lg text-xs h-8" />
                <Input value={t.description || ''} onChange={e => setClinicalTags(clinicalTags.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} className="rounded-lg text-xs h-8" />
                {!t.system && <button type="button" onClick={() => saveClinicalTags(clinicalTags.filter((_, i) => i !== idx))} className="text-[10px] text-red-500 hover:underline text-left" data-testid={`remove-clinical-tag-${t.key}`}>Remover etiqueta</button>}
              </div>
            ))}
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <Input value={newTag.label} onChange={e => setNewTag({ ...newTag, label: e.target.value })} placeholder="Nova etiqueta" className="rounded-lg text-xs h-8" data-testid="new-clinical-tag-label" />
              <Input value={newTag.description} onChange={e => setNewTag({ ...newTag, description: e.target.value })} placeholder="Descrição" className="rounded-lg text-xs h-8" />
            </div>
            <button onClick={() => { saveClinicalTags([...clinicalTags, ...(newTag.label.trim() ? [{ ...newTag, key: '' }] : [])]); setNewTag({ label: '', description: '' }); }} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid="save-clinical-tags-btn">Guardar etiquetas</button>
          </div>
          <div className="mt-4 border-t border-brand-border pt-3" data-testid="untagged-foods-list">
            <p className="text-xs font-semibold text-brand-text mb-1">Alimentos com etiqueta sugerida por aplicar ({untagged.count})</p>
            <button onClick={async () => { try { await axios.post(`${API}/api/admin/baby-foods/apply-clinical-tags`, {}); toast.success('Etiquetas aplicadas'); fetchClinical(); fetch(); } catch { toast.error('Erro ao aplicar etiquetas'); } }} className="mb-2 border border-brand-green text-brand-green rounded-full px-3 py-1 text-[10px] font-medium" data-testid="apply-clinical-tags-btn">Aplicar etiquetas sugeridas (só acrescenta)</button>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              {untagged.foods.map(f => (
                <p key={f.id} className="text-[10px] text-brand-muted">{f.name} <span className="text-brand-green">({f.suggested_tags.join(', ') || '-'})</span></p>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicates review dialog */}
      <Dialog open={showDuplicates} onOpenChange={setShowDuplicates}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-2xl" data-testid="duplicates-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Possíveis duplicados</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Nomes muito parecidos na biblioteca. Escolhe qual fica e junta, ou marca como "não é duplicado" se forem alimentos diferentes de propósito.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {duplicates.length === 0 && <p className="text-xs text-brand-muted">Sem duplicados por rever.</p>}
            {duplicates.map(cluster => (
              <div key={cluster.normalized_name} className="border border-brand-border rounded-2xl p-3" data-testid={`duplicate-cluster-${cluster.normalized_name.replace(/\s+/g, '-')}`}>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cluster.foods.length}, 1fr)` }}>
                  {cluster.foods.map(f => (
                    <label key={f.id} className={`border rounded-xl p-2.5 cursor-pointer text-xs ${((dupKeepChoice[cluster.normalized_name] || cluster.foods[0].id) === f.id) ? 'border-brand-green bg-brand-green/5' : 'border-brand-border'}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <input type="radio" name={`dup-${cluster.normalized_name}`} checked={(dupKeepChoice[cluster.normalized_name] || cluster.foods[0].id) === f.id}
                          onChange={() => setDupKeepChoice({ ...dupKeepChoice, [cluster.normalized_name]: f.id })} data-testid={`duplicate-keep-radio-${f.id}`} />
                        <span className="font-medium text-brand-text">{f.name}</span>
                      </div>
                      <p className="text-brand-muted">Grupo: {groupLabels[f.group] || f.group}</p>
                      <p className="text-brand-muted">{f.all_year ? 'Todo o ano' : `Meses: ${(f.months || []).join(', ') || '-'}`}</p>
                      <p className="text-brand-muted">{f.articles_count} artigo(s) · {f.image_path ? 'tem imagem' : 'sem imagem'}</p>
                      <p className="text-brand-muted">{f.is_allergen ? `Alergénio: ${foodCats(f).join(' + ')}` : 'Não alergénio'}</p>
                      <p className="text-brand-muted">{f.menus_count} ementa(s) · {f.food_introductions_count} introdução/ões</p>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => markNotDuplicate(cluster.foods.map(f => f.id))} className="text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5" data-testid={`not-duplicate-btn-${cluster.normalized_name.replace(/\s+/g, '-')}`}>Não é duplicado</button>
                  <button onClick={() => mergeDuplicates(cluster)} className="text-xs text-white bg-brand-green rounded-full px-3 py-1.5 font-medium" data-testid={`merge-duplicates-btn-${cluster.normalized_name.replace(/\s+/g, '-')}`}>Juntar</button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
